// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { IconFixedItem } from 'components/atoms/icons/IconFixedItem';
import { InputDataListField } from 'components/molecules/InputDataListField';
import i18n from 'i18n';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';
import {
  selectSubSiteId,
  selectTare,
  selectTenants,
  setTares,
} from 'redux/slices/siteInfoSlice';
import {
  selectIsUserSubEmissionSite,
  selectUser,
} from 'redux/slices/userSlice';
import { Path } from 'routes';
import { createAxios } from 'ts/createAxios';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import { DbManager } from 'util/DbManager';
import { CategoryItem, ItemType, TareData, TareItem } from 'util/Types';
import { handleError } from 'util/errorHandler';
import useNetworkConnection from 'util/hooks/useNetworkConnection';
import { useQuery } from 'util/queryParams';
import styles from './EmissionInput.module.scss';
import {
  validateInputEmissionNumber,
  validateMaxMinValue,
  validateSelectField,
} from 'util/validator';
import {
  DATE_FORMAT,
  MAXIMUM_TARE_SUPPORTED_MANUAL_WEIGHT,
  MAXIMUM_TARE_WEIGHT_WITHOUT_SCALE_MODEL,
  NETWORK_ERROR_MESSAGE,
  QUANTITY_INPUT_MAX_LENGTH,
  REGEX_OBJECT,
} from 'util/ConstantValues';
import { selectLoading } from 'redux/slices/loadingSlice';
import { EmissionConfirmModal } from 'components/molecules/EmissionConfirmModal';
import {
  fnGetDataSyncTime,
  getUnitLabel,
  mergeClasses,
  normalizeNumber,
} from 'util/commons';
import dayjs from 'dayjs';
import Toggle from 'components/atoms/Toggle';
import { useConnectToScale } from 'util/hooks/useConnectToScale';
import { useCheckTenantIdQuery } from 'util/hooks/useCheckTenantIdQuery';
import { useWeightInput } from 'util/hooks/useScaleWeightInput';
import { SelectOption } from '../EditCompressionsResourceDataModal';
import { SelectField } from 'components/molecules/SelectField';
import { Label } from 'components/atoms/Label';
import { API_ENDPOINTS } from 'util/endPoints';
import { OperatorType, ScaleType } from 'util/Enums';
import NumberWheel from 'components/molecules/NumberWheel';
import BigNumber from 'bignumber.js';

type Props = {
  emissionType: CategoryItem;
  afterSubmit: () => void;
};

type Input = {
  quantity?: string;
  gradeId?: number;
};

export type ScaleObject = {
  isSetScalesModel: boolean;
  tareMax: number;
  isDisplayTareInput: boolean;
};

// FIXME: not used
// const getInputUnit = (wasteUnit) => {
//   if (wasteUnit && wasteUnit['inputUnit']) {
//     return wasteUnit['inputUnit'];
//   }
//   return 'kg';
// };

export function EmissionInput({ emissionType, afterSubmit }: Props) {
  const dispatch = useDispatch();
  const api = createAxios();
  const user = useSelector(selectUser);
  const isUserSubEmissionSite = useSelector(selectIsUserSubEmissionSite);
  const history = useHistory();
  const tare = useSelector(selectTare);
  const [isConfirm, setIsConfirm] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<Partial<TareItem>>();
  const query = useQuery();
  const tenantId = query.get('tenantId');
  const { t } = useTranslation();
  const isOnline = useNetworkConnection();
  const tenants = useSelector(selectTenants);
  const { offlineInitialized } = useSelector(selectLoading);
  const { scaleConnected, handleChangeScaleConnectionStatus } =
    useConnectToScale();
  const subSiteId = useSelector(selectSubSiteId);
  const [gradeOpts, setGradeOpts] = useState<SelectOption[]>([]);

  const tareMax = useMemo(
    () =>
      Number(
        user.scalesModel?.tareMax || MAXIMUM_TARE_WEIGHT_WITHOUT_SCALE_MODEL
      ),
    [user.scalesModel]
  );

  const isDisplayTareInput = tareMax >= MAXIMUM_TARE_SUPPORTED_MANUAL_WEIGHT;

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === Number(tenantId)),
    [tenants, tenantId]
  );

  const { weightInputRef } = useWeightInput({
    receiveValueFromScaleCondition: !isConfirm,
    itemType: emissionType.type,
  });

  useCheckTenantIdQuery();

  const initInputData: Input = {
    quantity: '',
    gradeId: gradeOpts.length === 1 ? Number(gradeOpts[0].value) : undefined,
  };

  const fetchTares = async () => {
    if (!offlineInitialized || isUserSubEmissionSite) {
      return;
    }

    const cachedSites = await DbManager.sites.toArray();

    if (cachedSites.length) {
      const cachedTares = await DbManager.tares.toArray();

      dispatch(
        setTares({
          page: 0,
          total: cachedTares.length,
          tares: cachedTares,
        })
      );

      return;
    }

    api
      .get<TareData>(`/tares?siteId=${user.siteId}`)
      .then(async (res) => {
        dispatch(setTares(res.data));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const items = res.data.tares.map((tare: any) => ({
          ...tare,
          siteId: user.siteId,
        }));

        await DbManager.tares.clear();
        await DbManager.tares.bulkAdd(items);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleSelected = (tareItem: TareItem) => {
    setSelectedItem((prev) => {
      if (prev?.id === tareItem.id) {
        return { weight: '0' };
      }

      return tareItem;
    });
  };

  const fetchGradeOpts = async () => {
    if (!isUserSubEmissionSite) {
      return;
    }

    const commonApi = createAxios();

    await commonApi
      .get<{ id: number; name: string }[]>('/commons/grades', {
        params: {
          siteId: user.siteId,
        },
      })
      .then((res) => {
        setGradeOpts(
          res.data.map((i) => {
            return { label: i.name, value: i.id };
          })
        );
      })
      .catch(handleError);
  };

  useEffect(() => {
    fetchTares();
    fetchGradeOpts();
  }, [offlineInitialized]);

  const handleCallFunctionInit = () => {
    try {
      window && window.regisEmissionInitial();
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    handleCallFunctionInit();
  }, []);

  const convertedTares = useMemo(
    () =>
      [...(tare?.tares ?? [])]
        .filter(
          (item) =>
            (!tenantId ||
              !item.tenants?.length ||
              item.tenants.some((e) => e.id === Number(tenantId))) &&
            Number(item.weight) <= tareMax
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [tare, tenantId, tareMax]
  );

  const getConfirmQuantity = (quantity?: string) => {
    if (
      (emissionType.type === ItemType.OPTIONAL ||
        user.operatorType === OperatorType.Compressions) &&
      selectedItem
    ) {
      const subQuantity = Number(quantity) - Number(selectedItem.weight);

      return normalizeNumber({
        value: subQuantity,
        commaSplit: false,
      });
    }

    return quantity;
  };

  const afterAddNew = (isSubmitAndFinish?: boolean) => {
    setIsConfirm(false);
    if (!isSubmitAndFinish || (!user.isShowTenants && !isUserSubEmissionSite)) {
      afterSubmit();
    } else {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete('tenantId');

      history.push({
        pathname: Path.selectTenant,
        search: searchParams.toString(),
      });
    }
    toast.success(t('messages.M_109'));
  };

  const saveLocalWasteRegistrations = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    waste: any[],
    quantity?: string
  ) => {
    const total = Number(emissionType.convertKg) * Number(quantity);

    if (total.toString().length > 10) {
      toast.error(t('emission_input.weight_too_large'));
      return Promise.reject();
    }
    const categoryId =
      emissionType.categoryId != null
        ? Number(emissionType.categoryId)
        : undefined;
    let categoryName = emissionType.categoryName;

    if (categoryId != null && categoryName == null) {
      const cachedCategory = await DbManager.categories
        .where('id')
        .equals(categoryId)
        .first();

      if (cachedCategory) {
        categoryName = cachedCategory.name;
      }
    }

    // add wasteUnitName, tenantName, timeRegis
    const records = waste.map((record) => ({
      ...record,
      ...(categoryId != null &&
        categoryName != null && {
          categoryId,
          categoryName,
        }),
      type: emissionType.type,
      wasteUnitName: emissionType.name,
      tenantName: selectedTenant?.name,
      timeRegis: new Date().toISOString(),
      createdBy: user.userId,
    }));

    // save records to indexedDb
    await DbManager.wasteRegistrations.bulkAdd(records);

    return [
      {
        id: null,
        tenant: {
          id: selectedTenant?.id,
          name: selectedTenant?.name,
        },
        wasteUnit: {
          id: emissionType.id,
          name: emissionType.name,
        },
        emissionDatetime: dayjs().format(DATE_FORMAT.slaYMDHms),
        materialName: emissionType.materialName,
        site: {
          id: user.siteId,
          name: user.siteName,
        },
        weight: emissionType.convertKg
          ? total.toString()
          : quantity?.toString(),
      },
    ];
  };

  const getMeasuredWeight = (
    convertKg: string | number | null,
    quantity: string
  ) => {
    if (emissionType.type === ItemType.OPTIONAL) {
      return quantity;
    }

    if (!convertKg) return '0';

    return new BigNumber(convertKg).multipliedBy(quantity).toString();
  };

  const submitEmissions = async (
    values: Input,
    isSubmitAndFinish?: boolean
  ) => {
    const quantity = getConfirmQuantity(values.quantity);

    if (isNaN(Number(quantity))) {
      toast.error(t('emission_input.input_number'));
      return;
    }

    if (isUserSubEmissionSite) {
      api
        .post(API_ENDPOINTS.SUB_SITE_RESOURCES, {
          tenantId: Number(tenantId),
          gradeId: Number(values.gradeId),
          wasteUnitId: emissionType.id,
          weight: quantity,
          siteId: subSiteId,
          tareWeight: selectedItem?.weight,
        })
        .then(({ data }) => {
          afterAddNew(isSubmitAndFinish);

          if (user.operatorType === OperatorType.Compressions) {
            window?.postSubEmission?.(
              JSON.stringify({
                measuredWeight: normalizeNumber({
                  value: getMeasuredWeight(
                    emissionType.convertKg,
                    values.quantity || '0'
                  ),
                }),
                containerWeight: normalizeNumber({
                  value: selectedItem?.weight || 0,
                }),
                weight: data.weight,
                resourceId: emissionType.id,
                id: data.id,
                qrCode: data.qrCode,
                itemName: emissionType.name,
                registDatetime: getFormatDateWithCurrentTimezone(
                  data.registDatetime,
                  DATE_FORMAT.full_date
                ),
                grade: data.grade,
              }),
              i18n.language
            );
          }
        })
        .catch((err) => {
          if (err?.message.includes('Android')) {
            return;
          }

          handleError(err);
        });

      return;
    }

    const waste = [
      {
        type: emissionType.id,
        quantity: Number(quantity),
        wasteTypeMasterId: emissionType.wasteTypeMasterId,
        recycleMethodId: emissionType.recycleMethodId,
        ...(emissionType.id && { wasteUnitId: emissionType.id }),
        ...(tenantId && { tenantId: Number(tenantId) }),
        ...(emissionType.type === ItemType.OPTIONAL && {
          tareName: selectedItem?.name,
          tareWeight: selectedItem?.weight,
        }),
      },
    ];
    const dataSyncTime = await fnGetDataSyncTime();

    if (!dataSyncTime && isOnline) {
      api
        .post('/emissions', {
          waste,
        })
        .then((res) => {
          afterAddNew(isSubmitAndFinish);

          if (window && window.postEmissions) {
            window.postEmissions(
              JSON.stringify([
                {
                  ...res.data[0],
                  measuredWeight: normalizeNumber({
                    value: getMeasuredWeight(
                      emissionType.convertKg,
                      values.quantity || '0'
                    ),
                  }),
                  containerWeight: normalizeNumber({
                    value: selectedItem?.weight || 0,
                  }),
                  emissionDatetime: getFormatDateWithCurrentTimezone(
                    res.data[0].emissionDatetime,
                    DATE_FORMAT.slaYMDHms
                  ),
                },
              ]),
              i18n.language
            );
          }
        })
        .catch(async (e) => {
          if (e?.message.includes('Android')) {
            return;
          }

          if (e?.message === NETWORK_ERROR_MESSAGE) {
            saveLocalWasteRegistrations(
              waste,
              getConfirmQuantity(values.quantity)?.toString()
            ).then((data) => {
              afterAddNew(isSubmitAndFinish);
              window?.postEmissions?.(
                JSON.stringify(
                  data.map((d) => ({
                    ...d,
                    weight: normalizeNumber({
                      value: d.weight,
                    }),
                    measuredWeight: normalizeNumber({
                      value: getMeasuredWeight(
                        emissionType.convertKg,
                        values.quantity || '0'
                      ),
                    }),
                    containerWeight: normalizeNumber({
                      value: selectedItem?.weight || 0,
                    }),
                  }))
                ),
                i18n.language
              );
            });

            return;
          }

          handleError(e);
        });
    } else {
      saveLocalWasteRegistrations(
        waste,
        getConfirmQuantity(values.quantity)?.toString()
      ).then((data) => {
        afterAddNew(isSubmitAndFinish);
        window?.postEmissions?.(
          JSON.stringify(
            data.map((d) => ({
              ...d,
              weight: normalizeNumber({
                value: d.weight,
              }),
              measuredWeight: normalizeNumber({
                value: getMeasuredWeight(
                  emissionType.convertKg,
                  values.quantity || '0'
                ),
              }),
              containerWeight: normalizeNumber({
                value: selectedItem?.weight || 0,
              }),
            }))
          ),
          i18n.language
        );
      });
    }
  };

  return (
    <div className={styles.emissionInput}>
      {(user.operatorType === OperatorType.Emissions ||
        user.operatorType === OperatorType.Compressions) &&
        user.scalesType !== ScaleType.NONE && (
          <div className={styles.scaleConnectToggle}>
            <Toggle
              content={t('emission_input.connect_with_scale') || ''}
              value={scaleConnected}
              setValue={handleChangeScaleConnectionStatus}
              isDarkMode
            />
          </div>
        )}

      <Form<Input>
        initialValues={initInputData}
        validate={(values) => {
          return {
            quantity: validateInputEmissionNumber(
              values.quantity || '',
              emissionType.type
            ),
            ...(isUserSubEmissionSite && {
              grade: validateSelectField(t('common.grade'), values.gradeId),
            }),
          };
        }}
        onSubmit={(values) => {
          submitEmissions(values);
        }}
      >
        {({ handleSubmit, valid, values }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className={mergeClasses(styles.formInput, {
                [styles.formInputWithTare]:
                  emissionType.type === ItemType.OPTIONAL,
              })}
            >
              {selectedTenant && (
                <p className={styles.selectedTenant}>{selectedTenant.name}</p>
              )}

              {isUserSubEmissionSite && (
                <div className={styles.gradeSelect}>
                  <Label
                    className={styles.gradeSelectLabel}
                    text={t('common.grade') || ''}
                    required
                  />

                  <SelectField
                    placeholder={
                      gradeOpts.length === 1
                        ? t('common.grade')
                        : t('common.please_select')
                    }
                    name="gradeId"
                    isDarkmode
                    options={gradeOpts}
                    showPlaceholderOption
                  />
                </div>
              )}

              {(user.operatorType === OperatorType.Emissions ||
                user.operatorType === OperatorType.Compressions) &&
                user.scalesType !== ScaleType.NONE && (
                  <div className={styles.scaleConnectToggleMobile}>
                    <Toggle
                      content={t('emission_input.connect_with_scale') || ''}
                      value={scaleConnected}
                      setValue={handleChangeScaleConnectionStatus}
                      isDarkMode
                    />
                  </div>
                )}

              <div className={styles.inputType}>
                <div className={styles.commonInfo}>
                  <div className={styles.info}>
                    <div className={styles.labelBox}>
                      <div className={styles.label}>{emissionType.name}</div>
                      {emissionType.type === ItemType.FIX && (
                        <div className={styles.type}>
                          <div className={`${styles.itemType}`}>
                            <IconFixedItem />
                          </div>
                        </div>
                      )}
                    </div>

                    {emissionType.type === ItemType.FIX && (
                      <div className={styles.weightFix}>
                        {`${emissionType.convertKg}kg/${
                          emissionType.quantity
                        }${getUnitLabel(emissionType.unit)}`}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.weight}>
                <div className={styles.weightField}>
                  <InputDataListField
                    placeholder="0"
                    inputRef={weightInputRef}
                    name="quantity"
                    required
                    type="text"
                    maxlength={QUANTITY_INPUT_MAX_LENGTH}
                    hidePlaceholderOnFocus
                    validRegex={
                      emissionType.type === ItemType.FIX
                        ? REGEX_OBJECT.fiveDigits
                        : REGEX_OBJECT.fiveDigitsWith2Decimal
                    }
                  />
                  {emissionType.type !== ItemType.FIX && (
                    <span className={styles.weightUnit}>kg</span>
                  )}
                </div>
              </div>

              <button
                className={`${styles.submitButton} ${
                  !valid ? styles.disableButton : ''
                }`}
                disabled={!valid}
                type="button"
                onClick={() => {
                  setIsConfirm(true);
                  return false;
                }}
              >
                {t('emission_input.button_submit')}
              </button>
              {user.operatorType === OperatorType.Compressions && (
                <div className={styles.emissionInputTareBox}>
                  <div className={styles.tareInput}>
                    <InputDataListField
                      placeholder={t('emission_input.container_weight')}
                      name="tareWeight"
                      validRegex={REGEX_OBJECT.fiveDigitsWith2Decimal}
                      isDarkMode
                      className={styles.tareWeightInput}
                      handleChange={(value) =>
                        setSelectedItem({ weight: String(value) })
                      }
                      validate={(value) =>
                        validateMaxMinValue({
                          value,
                          field: t('emission_input.container_weight'),
                          maxValue: tareMax,
                          checkRequired: false,
                        })
                      }
                    />
                    <div className={styles.tareInputUnit}>kg</div>
                  </div>
                </div>
              )}

              {emissionType.type === ItemType.OPTIONAL &&
                (convertedTares.length > 0 ||
                  user.operatorType === OperatorType.Emissions) && (
                  <div
                    className={mergeClasses(styles.emissionTareBox, {
                      [styles.emissionInputTareBox]: isDisplayTareInput,
                    })}
                  >
                    {convertedTares.length > 0 && (
                      <div className={styles.emissionTare}>
                        {convertedTares.map((item) => (
                          <span
                            key={item.id}
                            className={mergeClasses({
                              [styles.emissionSelected]:
                                selectedItem?.id === item.id,
                            })}
                            onClick={() => {
                              handleSelected(item);
                            }}
                          >
                            {`${
                              item.name.length > 20
                                ? `${item.name.slice(
                                    0,
                                    18 - item.weight.length
                                  )}...`
                                : item.name
                            } (${normalizeNumber({
                              value: item.weight,
                            })}kg)`}
                          </span>
                        ))}
                      </div>
                    )}

                    {user.operatorType === OperatorType.Emissions && (
                      <div className={styles.tareInputBox}>
                        <div className={styles.tareInputTitle}>
                          {t('emission_input.container_weight')}
                        </div>
                        <div className={styles.tareInput}>
                          {isDisplayTareInput ? (
                            <InputDataListField
                              placeholder={t('emission_input.container_weight')}
                              name="tareWeight"
                              validRegex={REGEX_OBJECT.fiveDigitsWith2Decimal}
                              value={selectedItem?.weight}
                              isDarkMode
                              className={styles.tareWeightInput}
                              handleChange={(value) =>
                                setSelectedItem({ weight: String(value) })
                              }
                              validate={(value) =>
                                validateMaxMinValue({
                                  value: selectedItem?.weight || value,
                                  field: t('emission_input.container_weight'),
                                  maxValue: tareMax,
                                  checkRequired: false,
                                })
                              }
                            />
                          ) : (
                            <NumberWheel
                              value={Number(selectedItem?.weight || '0')}
                              separator="."
                              onChange={(value) =>
                                setSelectedItem({ weight: String(value) })
                              }
                              max={tareMax}
                              decimalPlaces={user.scalesModel ? 2 : 1}
                            />
                          )}
                          <div className={styles.tareInputUnit}>kg</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              <EmissionConfirmModal
                isOpen={isConfirm}
                emissionType={emissionType}
                closeEditModal={() => setIsConfirm(false)}
                onClickSubmitAndFinish={() => {
                  submitEmissions(values, true);
                }}
                onClick={handleSubmit}
                quantity={`${getConfirmQuantity(values.quantity)}`}
                measuredWeight={values.quantity || ''}
                tare={selectedItem}
                tenantName={selectedTenant?.name}
              />
            </form>
          );
        }}
      </Form>
    </div>
  );
}
